import {AfterViewInit, Component, forwardRef, Injector, Input, OnInit, ViewChild} from '@angular/core';
import {NgbDatepicker, NgbPopover, NgbPopoverConfig, NgbTimeStruct} from '@ng-bootstrap/ng-bootstrap';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {noop} from 'rxjs';
import {Guid} from "../models/Guid";
import {DateTime} from "luxon";
import {DateHelpers} from "../helpers/date-helpers";

@Component({
  selector: 'app-date-time-picker',
  templateUrl: './date-time-picker.component.html',
  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true
    }
  ],
  host: {
    class: 'd-flex w-100'
  }
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input() inputDatetimeFormat: 'short' | 'medium' | 'shortDate' | 'mediumDate' | 'dd.MM.yyyy' | 'dd.MM.yyyy HH' | 'dd.MM.yyyy HH:mm' | 'dd.MM.yyyy HH:mm:ss' = 'dd.MM.yyyy HH:mm:ss';
  @Input() inputTimeFormat = 'HH:mm:ss';
  @Input() inputClass = '';
  @Input() hourStep = 1;
  @Input() minuteStep = 1;
  @Input() secondStep = 1;
  @Input() seconds = true;
  @Input() disabled = false;
  @Input() showTime: boolean = true;
  @Input() name = Guid.create();
  @Input() isInvalid = false;
  showTimePicker = false;
  datetime?: Date;
  time = {hour: 0, minute: 0, second: 0} as NgbTimeStruct;
  @ViewChild(NgbDatepicker, {static: true}) private dp: NgbDatepicker;
  @ViewChild(NgbPopover) private popover: NgbPopover;

  private onTouched: () => void = noop;
  private onChange: (_: any) => void = noop;

  ngControl: NgControl;
  width: string;

  constructor(private config: NgbPopoverConfig, private inj: Injector) {
    config.autoClose = 'outside';
    config.placement = 'auto';
  }

  ngOnInit(): void {
    this.ngControl = this.inj.get(NgControl);
    this.width = this.getMinWidth();
  }

  ngAfterViewInit(): void {
    this.popover.hidden.subscribe($event => {
      this.showTimePicker = false;
    });
  }

  writeValue(newModel?: Date) {
    this.datetime = newModel;
    if (typeof this.datetime === 'string') {
      this.datetime = DateTime.fromISO(this.datetime).toJSDate();
    }
    this.time = DateHelpers.getNgbTimeStruct(this.datetime);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  toggleDateTimeState($event) {
    this.showTimePicker = !this.showTimePicker;
    this.time = DateHelpers.getNgbTimeStruct(this.datetime);
    $event.stopPropagation();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange($event: any) {
    const value: string = $event.target.value;
    if (value === '') {
      this.datetime = null;
    } else {
      const parsed = DateTime.fromFormat(value, this.inputDatetimeFormat);
      this.datetime = parsed.isValid ? parsed.toJSDate() : new Date();
      this.time = DateHelpers.getNgbTimeStruct(this.datetime);
    }
    this.onChange(this.datetime);
  }

  onDateChange($event: Date) {
    this.datetime = $event;
    this.time = DateHelpers.getNgbTimeStruct(this.datetime);
    this.onChange(this.datetime);
    if (!this.showTime) {
      this.popover.close();
    }
  }

  onTimeChange(event: NgbTimeStruct) {
    this.datetime ??= new Date();
    this.datetime.setHours(event.hour, event.minute, event.second);
    this.datetime = new Date(this.datetime);
    this.onChange(this.datetime);
  }

  inputBlur($event) {
    this.onTouched();
  }

  getStartDate() {
    if (this.datetime) {
      return {year: this.datetime.getFullYear(), month: this.datetime.getMonth() + 1, day: this.datetime.getDay()};
    }
    return null;
  }

  getMinWidth() {
    switch (this.inputDatetimeFormat) {
      case 'short': return '156px';
      case 'medium': return '212px';
      case 'shortDate': return '120px';
      case 'mediumDate': return '160px';
      case 'dd.MM.yyyy': return '134px';
      case 'dd.MM.yyyy HH:mm': return '172px';
      case 'dd.MM.yyyy HH:mm:ss': return '190px';
      default: return '150px';
    }
  }
}
